import type { Message, MessagePoll } from '@nexo/types';
import { Button, cn, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BarChart3, Check, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { formatDay, formatNumber, formatTime } from '../../lib/format';
import { errorMessage } from '../../lib/forms';
import { canIn } from '../../lib/permissions';
import { useMe } from '../auth/AuthProvider';

/** A poll in the chat: pick an option (or several), see the results live. */
export function PollCard({ message, poll }: { message: Message; poll: MessagePoll }) {
  const { t } = useTranslation();
  const me = useMe();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { channels, memberById } = useTeam();
  const channel = channels.find((c) => c.id === message.channelId);
  const canVote = !poll.closed && canIn(channel, 'add_reactions');
  const canClose =
    !poll.closed && (message.author.id === me.id || canIn(channel, 'manage_messages'));

  const mine = poll.options.filter((o) => o.voterIds.includes(me.id)).map((o) => o.id);
  const voters = new Set(poll.options.flatMap((o) => o.voterIds));
  const totalVotes = poll.options.reduce((sum, o) => sum + o.voterIds.length, 0);
  const top = Math.max(0, ...poll.options.map((o) => o.voterIds.length));

  const onError = (err: unknown) =>
    toast({ title: t('No se pudo votar'), description: errorMessage(err), tone: 'error' });
  const vote = useMutation({
    mutationFn: (optionIds: string[]) => api.messages.vote(message.id, optionIds),
    onSuccess: (updated) => cache.upsertMessage(queryClient, updated),
    onError,
  });
  const close = useMutation({
    mutationFn: () => api.messages.closePoll(message.id),
    onSuccess: (updated) => cache.upsertMessage(queryClient, updated),
    onError,
  });

  const toggle = (optionId: string) => {
    if (!canVote) return;
    const picked = mine.includes(optionId);
    vote.mutate(
      poll.multiple
        ? picked
          ? mine.filter((id) => id !== optionId)
          : [...mine, optionId]
        : picked
          ? []
          : [optionId],
    );
  };

  const namesOf = (ids: string[]) =>
    ids
      .map((id) => (id === me.id ? t('Tú') : memberById(id)?.displayName))
      .filter(Boolean)
      .join(', ');

  return (
    <div className="mt-1 mb-1 max-w-md rounded-xl border border-border bg-surface p-4 shadow-panel">
      <p className="flex items-start gap-2 font-semibold">
        <BarChart3 className="mt-0.5 size-4 shrink-0 text-accent" />
        <span>{poll.question}</span>
      </p>
      <p className="mt-1 text-xs text-muted">
        {poll.closed
          ? t('Encuesta cerrada')
          : poll.multiple
            ? t('Puedes elegir varias opciones')
            : t('Elige una opción')}
        {!poll.closed &&
          poll.closesAt &&
          ` · ${t('Cierra: {{day}}, {{time}}', { day: formatDay(poll.closesAt).toLowerCase(), time: formatTime(poll.closesAt) })}`}
      </p>
      <ul className="mt-3 flex flex-col gap-2">
        {poll.options.map((option) => {
          const count = option.voterIds.length;
          const share = totalVotes ? Math.round((count / totalVotes) * 100) : 0;
          const picked = mine.includes(option.id);
          const winner = poll.closed && count > 0 && count === top;
          return (
            <li key={option.id}>
              <button
                type="button"
                disabled={!canVote || vote.isPending}
                onClick={() => toggle(option.id)}
                title={count > 0 ? namesOf(option.voterIds) : undefined}
                aria-pressed={picked}
                className={cn(
                  'relative flex w-full items-center gap-2.5 overflow-hidden rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                  picked ? 'border-accent' : 'border-border',
                  canVote && 'hover:border-border-strong',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'absolute inset-y-0 left-0 transition-[width] duration-500',
                    winner || picked ? 'bg-accent-soft' : 'bg-surface-2',
                  )}
                  style={{ width: `${share}%` }}
                />
                <span
                  className={cn(
                    'relative flex size-4 shrink-0 items-center justify-center border',
                    poll.multiple ? 'rounded' : 'rounded-full',
                    picked ? 'border-accent bg-accent text-accent-fg' : 'border-border-strong',
                  )}
                >
                  {picked && <Check className="size-3" />}
                </span>
                <span className={cn('relative min-w-0 flex-1', winner && 'font-semibold')}>
                  {option.text}
                </span>
                <span className="relative text-xs text-muted tabular-nums">
                  {share}% · {formatNumber(count)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted">
        <span>
          {voters.size === 1
            ? t('1 persona ha votado')
            : t('{{count}} personas han votado', { count: voters.size })}
        </span>
        {canClose && (
          <Button
            size="sm"
            variant="ghost"
            icon={<Lock className="size-3.5" />}
            loading={close.isPending}
            onClick={() => close.mutate()}
          >
            {t('Cerrar encuesta')}
          </Button>
        )}
      </div>
    </div>
  );
}
